import { useEffect, useState } from 'react';

import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';

import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { createTheme, ThemeProvider } from '@mui/material/styles';

import { collectionGroup, collection, getDocs, query, where, documentId, getDoc, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebase';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import startOfDay from 'date-fns/startOfDay';
import AdminNavbar from '../../components/AdminNavbar';

import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { FormControl, InputLabel, Stack } from '@mui/material';
import { sumBy } from 'lodash-es';
import { useAuthUser } from '../../UserAuthContext';

const theme = createTheme();

const locationCollection = collection(db, 'locations');

export default function Stats() {
  const defaultState = {
    totalGuests: "...",
    totalGroups: "...",
    answers: {},
    // totalTicketsSold: "...",
    // totalSalesCents: "...",
    averageLengthOfStay: "...",
    averageDurationInside: "...",
    totalSalesForThatDay: "...",
    totalSumSalesForThatDay: "...",
    ticketTypesSold: [],
    customersBought: [],
  }

  const [stats, setStats] = useState(defaultState);

  const [from, setFromValue] = useState(startOfDay(new Date(new Date().setHours(0, 0, 0, 0))));
  const [to, setToValue] = useState(startOfDay(new Date(new Date().setHours(0, 0, 0, 0) + 24 * 60 * 60 * 1000)));

  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("");
  const {userInfo} = useAuthUser();

  useEffect(() => {
    (async () => {
      // Tickets sold for that day
      let totalSalesForThatDay = 0;
      let totalSumSalesForThatDay = 0;
      let answers = {};
      let customersBought = [];
      let ticketTypesSold = [];

      const dateFrom = format(from, 'yyyy-MM-dd')
      const dateTo = format(to, 'yyyy-MM-dd')

      const statsDocs = await getDocs(
        query(
          collection(db, 'stats'),
          where("date", ">=", dateFrom),
          where("date", "<=", dateTo),
        )
      )

      if (!statsDocs.empty) {
        // merge all stats docs into one
        const data = statsDocs.docs.reduce((acc, cur) => {
          const curData = cur.data();
          return {
            totalTickets: acc.totalTickets + curData.totalTickets,
            totalSales: acc.totalSales + curData.totalSales,
            // sum answers from all docs in one line
            answers: Object.keys(curData.answers ?? {}).reduce((acc, key) => {
              acc[key] = (acc[key] ?? 0) + (curData.answers[key] ?? 0);
              return acc;
            }, acc.answers),
            customers: [...new Map([...acc.customers, ...curData.customers].map(customer => [customer.id, customer])).values()],
            tickets: [...acc.tickets, ...curData.tickets],
            // sum sales by location
            totalSalesByLocation: Object.keys(curData.totalSalesByLocation ?? {}).reduce((acc, key) => {
              acc[key] = (acc[key] ?? 0) + (curData.totalSalesByLocation[key] ?? 0);
              return acc;
            }, acc.totalSalesByLocation),
          }
        }, {
          totalTickets: 0,
          totalSales: 0,
          answers: {},
          customers: [],
          tickets: [],
          totalSalesByLocation: {}
        });

        const customers = data?.customers?.filter((customer) => {
          if (location !== 'all') {
            return customer.location === location;
          }

          return true;
        }) ?? [];
        const tickets = data?.tickets?.filter((ticket) => {
          if (location !== 'all') {
            return ticket.location === location;
          }

          return true;
        }) ?? [];
        totalSalesForThatDay = location !== 'all' ? tickets.length : data.totalTickets;
        totalSumSalesForThatDay = location !== 'all' ? data?.totalSalesByLocation?.[location] : data.totalSales;
        customersBought = customers;
        answers = data.answers;
        ticketTypesSold = tickets.reduce((acc, cur) => {
          const indexFound = acc.findIndex(a => a.title === cur.title);
          if (indexFound === -1) {
            // add new
            acc.push({ title: cur.title, qty: 1, total: cur.price })
          } else {
            // update existing
            acc[indexFound].qty += 1;
            acc[indexFound].total += cur.price;
          }
          return acc;
        }, []).sort((a, b) => a.title > b.title ? 1 : -1);
      }

      // Que stats
      const q = query(
        collection(db, 'ques/sydney/guests'),
        where("status", "==", "completed"),
        // Range bewteen selected day start and end
        where("completed", ">=", from.getTime()),
        where("completed", "<", from.getTime() + 24 * 60 * 60 * 1000)
      )

      const queQuerySnapshot = await getDocs(q);
      const totalGuests = queQuerySnapshot.docs.reduce((m, d) => {
        const groupSize = Number(d.data().groupSize);
        return m + (isNaN(groupSize) ? 0 : groupSize)
      }, 0);

      setStats({
        ...stats,
        answers,
        totalGroups: queQuerySnapshot.docs.length,
        totalGuests,
        // totalTicketsSold,
        // totalSalesCents,
        totalSalesForThatDay,
        totalSumSalesForThatDay,
        ticketTypesSold,
        customersBought
      })

    })();
  }, [from, to, location])

  const handleFromChange = (newValue) => {
    setStats(defaultState);
    setFromValue(startOfDay(newValue));
  };

  const handleToChange = (newValue) => {
    setStats(defaultState);
    setToValue(startOfDay(newValue));
  };

  const handleLocationChange = (event) => {
    setLocation(event.target.value);
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(query(locationCollection), (querySnapshot) => {
      const list = [];
      querySnapshot.forEach((doc) => {
        list.push({ ...doc.data(), id: doc.id });
      });

      if(userInfo?.locations) {
        // If user has locations filter the list
        const filteredList = list.filter((location) => userInfo.locations.includes(location.id));
        setLocations(filteredList);
        setLocation(filteredList[0]?.id ?? "");
        return;
      }

      // Otherwise display all
      list.unshift({
        id: "all",
        name: "All",
      });

      setLocations(list);
      setLocation("all");
    });

    return () => {
      unsubscribe();
    }


  }, [userInfo]);

  return (
    <>
      <AdminNavbar />
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              marginTop: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Stack direction="row" spacing={2} justifyContent="space-between" minWidth="100%">
                  <Box style={{ width: "66%" }}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <MobileDatePicker
                        key={"1"}
                        label="Date from"
                        inputFormat="yyyy/MM/dd"
                        value={from}
                        onChange={handleFromChange}
                        renderInput={(params) => <TextField style={{ width: "50%" }} {...params} />}
                      />
                      <MobileDatePicker
                        key={"2"}
                        label="Date to"
                        inputFormat="yyyy/MM/dd"
                        value={to}
                        onChange={handleToChange}
                        renderInput={(params) => <TextField style={{ width: "50%" }} {...params} />}
                      />
                    </LocalizationProvider>
                  </Box>
                  <FormControl style={{ width: "33%" }}>
                    <InputLabel>Location</InputLabel>
                    <Select
                      value={location}
                      label="Location"
                      onChange={handleLocationChange}
                      style={{ minWidth: "100%" }}
                    >
                      {
                        locations.map((location) => <MenuItem key={location.id} value={location.id}>{location.name}</MenuItem>)
                      }
                    </Select>
                  </FormControl>
                </Stack>

                <Grid item xs={12}>
                  <Typography sx={{ textAlign: 'center' }} component="div">
                    Total guests {stats.totalGuests}
                  </Typography>
                  <Typography sx={{ textAlign: 'center' }} component="div">
                    Total groups {stats.totalGroups}
                  </Typography>
                  {/* <Typography sx={{textAlign: 'center'}} component="div">
                  Total tickets sold on this day {stats.totalTicketsSold}
                </Typography> */}
                  <Typography sx={{ textAlign: 'center' }} component="div">
                    Total tickets for this day {stats.totalSalesForThatDay}
                  </Typography>
                  <Typography sx={{ textAlign: 'center' }} component="div">
                    Total sales for this day ${stats.totalSumSalesForThatDay}
                  </Typography>

                  {/* <Typography sx={{textAlign: 'center'}} component="div">
                  Total sales {!isNaN(stats.totalSalesCents) ? `$${stats.totalSalesCents/100}` : stats.totalSalesCents}
                </Typography> */}
                  {/* <Typography sx={{textAlign: 'center'}} component="div">
                  Avg. wait time {stats.averageLengthOfStay}
                </Typography>
                <Typography sx={{textAlign: 'center'}} component="div">
                  Avg. duration inside {stats.totalGavroups}
                </Typography> */}
                </Grid>

                <Grid item xs={12}>
                  <div style={{ width: '100%' }}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell><b>Ticket</b></TableCell>
                            <TableCell align="right"><b>Qty</b></TableCell>
                            <TableCell align="right"><b>Total</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            stats.ticketTypesSold?.length > 0 ? <>
                              {stats.ticketTypesSold.map((row) => (
                                <TableRow
                                  key={row.title}
                                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                  <TableCell component="th" scope="row">
                                    {row.title}
                                  </TableCell>
                                  <TableCell align="right">{row.qty}</TableCell>
                                  <TableCell align="right">{row.total}$</TableCell>
                                </TableRow>
                              ))}
                            </> : <>
                              <TableRow
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell component="th" scope="row" colSpan={3}>
                                  <Typography textAlign={"center"} variant="body1">No data.</Typography>
                                </TableCell>
                              </TableRow>
                            </>
                          }

                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <div style={{ width: '100%' }}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell><b>Answer</b></TableCell>
                            <TableCell align="right"><b>Total</b></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {
                            Object.keys(stats.answers).length > 0 ? <>
                              { Object.keys(stats.answers).map((key) => (
                                <TableRow
                                  key={key}
                                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                >
                                  <TableCell component="th" scope="row">
                                    {key}
                                  </TableCell>
                                  <TableCell align="right">{stats.answers[key]}</TableCell>
                                </TableRow>
                              ))}
                            </> : <>
                              <TableRow
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell component="th" scope="row" colSpan={3}>
                                  <Typography textAlign={"center"} variant="body1">No data.</Typography>
                                </TableCell>
                              </TableRow>
                            </>
                          }

                        </TableBody>
                      </Table>
                    </TableContainer>
                  </div>
                </Grid>

                <Grid item xs={12}>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><b>Customer</b></TableCell>
                          <TableCell align="right"><b>Action</b></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {
                          stats.ticketTypesSold?.length > 0 ? <>
                            {stats.customersBought.map((row) => (
                              <TableRow
                                key={row.id}
                                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                              >
                                <TableCell component="th" scope="row">
                                  {row.name}
                                </TableCell>
                                <TableCell align="right"><Link to={`/edit-user-data/${row.id}`}>Edit</Link></TableCell>
                              </TableRow>
                            ))}
                          </> : <>
                            <TableRow
                              sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                            >
                              <TableCell component="th" scope="row" colSpan={3}>
                                <Typography textAlign={"center"} variant="body1">No data.</Typography>
                              </TableCell>
                            </TableRow>
                          </>
                        }
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

              </Grid>

            </Box>

          </Box>
        </Container>
      </ThemeProvider>
    </>
  );
}
